
import React, { useState, useEffect, useRef } from 'react';
import {
  Briefcase, ChevronRight, ChevronLeft, CheckCircle, Rocket,
  Store, Code, Stethoscope, GraduationCap, Truck, Utensils,
  Wrench, ShoppingBag, MoreHorizontal, Lock, Loader2,
} from 'lucide-react';


import { COUNTRY_CODES, POPULAR_COUNTRIES } from '../utils/countryCodes';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

function detectCountryCode(): string {
  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && COUNTRY_CODES[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached && COUNTRY_CODES[cached]) return cached;

  try {
    const langRegion = navigator.language?.split('-')[1]?.toUpperCase();
    if (langRegion && COUNTRY_CODES[langRegion]) {
      sessionStorage.setItem('detected_country', langRegion);
      return langRegion;
    }
  } catch {}

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN','Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Tokyo':'JP','Asia/Seoul':'KR',
    'Asia/Shanghai':'CN','Asia/Hong_Kong':'HK','Asia/Taipei':'TW',
    'Europe/London':'GB','Europe/Dublin':'IE','Europe/Paris':'FR','Europe/Berlin':'DE',
    'Europe/Amsterdam':'NL','Europe/Madrid':'ES','Europe/Rome':'IT','Europe/Zurich':'CH',
    'America/New_York':'US','America/Chicago':'US','America/Los_Angeles':'US',
    'America/Toronto':'CA','America/Sao_Paulo':'BR','America/Mexico_City':'MX',
    'Australia/Sydney':'AU','Australia/Melbourne':'AU','Pacific/Auckland':'NZ',
    'Africa/Cairo':'EG','Africa/Lagos':'NG','Africa/Nairobi':'KE','Africa/Johannesburg':'ZA',
  };
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}

function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && COUNTRY_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (COUNTRY_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(COUNTRY_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length >= fallbackDialCode.length + 5) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const dial = parsed.dialCode.replace(/\D/g, '');
  const total = `${dial}${national}`;

  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dial}${national}`;
}

function CountryPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allCodes = Object.keys(COUNTRY_CODES);
  const filtered = search
    ? allCodes.filter(c => c.toLowerCase().includes(search.toLowerCase()) || COUNTRY_CODES[c]?.includes(search.replace('+', '')))
    : [...POPULAR_COUNTRIES, ...allCodes.filter(c => !POPULAR_COUNTRIES.includes(c))];

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-l-lg px-2.5 py-2.5 text-xs font-bold text-slate-700 cursor-pointer min-w-[64px] whitespace-nowrap hover:bg-slate-200 transition-colors"
      >
        +{COUNTRY_CODES[value] || '91'} <span className="text-[9px] opacity-60">▾</span>
      </button>

      {open && (
        <div
          onClick={e => e.stopPropagation()}
          className="absolute top-full left-0 z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-48 max-h-60 overflow-hidden flex flex-col mt-1"
        >
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              placeholder="Search country..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                className={`flex items-center gap-2 w-full px-3 py-1.5 text-left text-xs cursor-pointer hover:bg-slate-50 ${c === value ? 'bg-blue-50 font-bold text-blue-600' : 'text-slate-700'}`}
              >
                <span className="font-bold min-w-[36px]">+{COUNTRY_CODES[c]}</span>
                <span className="truncate">{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PhoneInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const defaultCountry = detectCountryCode();
  const parsed = parsePhoneValue(value, defaultCountry);

  const [country, setCountry] = useState(() => {
    if (value && value.startsWith('+')) return parsed.country;
    return defaultCountry;
  });
  const [number, setNumber] = useState(() => parsed.nationalNumber);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    if (!value) {
      setNumber('');
    } else {
      const p = parsePhoneValue(value, defaultCountry);
      setCountry(p.country);
      setNumber(p.nationalNumber);
    }
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const dialCode = COUNTRY_CODES[newCountry] || '91';
    const cleanDigits = number.trim();
    const formatted = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  const handleNumberChange = (raw: string) => {
    let cleanNum = raw.replace(/\D/g, '');
    const dialCode = (COUNTRY_CODES[country] || '91').replace(/\D/g, '');

    if (raw.includes('+') && cleanNum.startsWith(dialCode)) {
      cleanNum = cleanNum.slice(dialCode.length);
    } else if (cleanNum.startsWith(dialCode) && cleanNum.length > 10) {
      cleanNum = cleanNum.slice(dialCode.length);
    }

    setNumber(cleanNum);
    const formatted = cleanNum ? `+${dialCode}${cleanNum}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  return (
    <div className="flex w-full items-center">
      <CountryPicker value={country} onChange={handleCountryChange} />
      <input
        type="tel"
        value={number}
        onChange={e => handleNumberChange(e.target.value)}
        placeholder="98765 43210"
        className="w-full bg-slate-50 border border-l-0 border-slate-200 rounded-r-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
      />
    </div>
  );
}

interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: string;
  org_id: number | null;
}

interface Props {
  token: string;
  authUser: AuthUser | null;
  onComplete: () => void;
}

const INDIVIDUAL_PROFESSIONS = [
  { id: 'freelancer',  label: 'Freelancer',       icon: <Code className="w-4 h-4" /> },
  { id: 'consultant',  label: 'Consultant',        icon: <Briefcase className="w-4 h-4" /> },
  { id: 'doctor',      label: 'Doctor / Clinic',   icon: <Stethoscope className="w-4 h-4" /> },
  { id: 'tutor',       label: 'Tutor / Educator',  icon: <GraduationCap className="w-4 h-4" /> },
  { id: 'trader',      label: 'Trader / Reseller', icon: <ShoppingBag className="w-4 h-4" /> },
  { id: 'contractor',  label: 'Contractor',        icon: <Wrench className="w-4 h-4" /> },
  { id: 'other',       label: 'Other',             icon: <MoreHorizontal className="w-4 h-4" /> },
];

const ORG_INDUSTRIES = [
  { id: 'retail',     label: 'Retail / Shop',         icon: <Store className="w-4 h-4" /> },
  { id: 'restaurant', label: 'Restaurant / Cafe',     icon: <Utensils className="w-4 h-4" /> },
  { id: 'tech',       label: 'Tech / Software',       icon: <Code className="w-4 h-4" /> },
  { id: 'logistics',  label: 'Logistics / Delivery',  icon: <Truck className="w-4 h-4" /> },
  { id: 'healthcare', label: 'Healthcare',            icon: <Stethoscope className="w-4 h-4" /> },
  { id: 'education',  label: 'Education',             icon: <GraduationCap className="w-4 h-4" /> },
  { id: 'services',   label: 'Professional Services', icon: <Briefcase className="w-4 h-4" /> },
  { id: 'other',      label: 'Other',                 icon: <MoreHorizontal className="w-4 h-4" /> },
];

const TEAM_SIZES     = ['Just me', '2–5', '6–20', '21–50', '50+'];
const INVOICE_VOLUMES = ['< 10 / month', '10–50 / month', '50–200 / month', '200+ / month'];

export default function OnboardingPage({ token, authUser, onComplete }: Props) {
  const isOrg = authUser?.role === 'owner' || authUser?.role === 'organization';
const totalSteps = isOrg ? 4 : 3;
const invoiceStep = isOrg ? 4 : 3;

  const [step, setStep]       = useState(1);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // Individual fields
  const [profession,   setProfession]   = useState('');
  const [displayName,  setDisplayName]  = useState(authUser?.name || '');
  const [phone,        setPhone]        = useState('');

  // Org fields
  const [companyName,    setCompanyName]    = useState('');
  const [industry,       setIndustry]       = useState('');
  const [teamSize,       setTeamSize]       = useState('');
  const [invoiceVolume,  setInvoiceVolume]  = useState('');
  const [website,        setWebsite]        = useState('');

  // Assigned prefixes — fetched from backend
  const [invoicePrefix,  setInvoicePrefix]  = useState('');

  const [receiptPrefix,  setReceiptPrefix]  = useState('');
  const [prefixLoading,  setPrefixLoading]  = useState(false);
  const [prefixError,    setPrefixError]    = useState('');

  // Fetch / claim prefix when user reaches the last step
  useEffect(() => {
    if (step !== invoiceStep) return;
    if (invoicePrefix) return; // already loaded

    const claim = async () => {
      setPrefixLoading(true);
      setPrefixError('');
      try {
        // Derive a sensible default prefix from the name / company name
        const raw = (isOrg ? companyName : displayName || authUser?.name || 'INV');
        const desired = raw
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '')
          .slice(0, 3) || 'INV';

        const invRes = await fetch(`${BASE}/invoices/claim-prefix`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ prefix: desired }),
        });

        const inv = await invRes.json();
        const assignedInv = inv.prefix || desired;
        const assignedRcp = assignedInv + 'R';

        setInvoicePrefix(assignedInv);
        setReceiptPrefix(assignedRcp);

        localStorage.setItem('invoice_prefix_locked', assignedInv);
        localStorage.setItem('receipt_prefix_locked', assignedRcp);
      } catch {
        setPrefixError('Could not assign prefix. Check your connection.');
      } finally {
        setPrefixLoading(false);
      }
    };

    claim();
  }, [step]);

  const progress = Math.round((step / totalSteps) * 100);

  const canNext = () => {
    if (isOrg) {
      if (step === 1) return companyName.trim().length > 0;
      if (step === 2) return industry.length > 0;
    } else {
      if (step === 1) return profession.length > 0;
      if (step === 2) return displayName.trim().length > 0;
    }
    return true;
  };

  const next = () => { setError(''); setStep(s => s + 1); };
  const back = () => { setError(''); setStep(s => s - 1); };

  const handleSubmit = async () => {
    const cleanPhone = phone.trim() ? getCleanE164Phone(phone) : '';
    if (phone.trim() && !cleanPhone) {
      setError('Please enter a valid phone number (between 7 and 15 digits) or leave it blank.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const body = isOrg
        ? { company_name: companyName, industry, team_size: teamSize, invoice_volume: invoiceVolume, domain: website, contacts: [] }
        : { company_name: displayName, profession, phone: cleanPhone, contacts: [] };

      const res = await fetch(`${BASE}/auth/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to save');

      onComplete();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center px-4 py-10"
      style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      <div className="bg-white rounded-2xl border border-slate-200 shadow-lg w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden">
  <img src="/logo-icon.png" alt="CloudAIRambo" className="w-8 h-8 object-contain" />
</div>
            <div>
              <h1 className="font-extrabold text-lg leading-tight">
                Welcome, {authUser?.name?.split(' ')[0] || 'there'}!
              </h1>
              <p className="text-blue-100 text-xs mt-0.5">
                {isOrg ? 'Set up your organization' : 'Set up your personal workspace'}
              </p>
            </div>
            <div className="ml-auto text-right shrink-0">
              <span className="text-xs text-blue-200 font-medium">Step {step} of {totalSteps}</span>
            </div>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* ── INDIVIDUAL Step 1: Profession ── */}
          {!isOrg && step === 1 && (
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">What best describes your work?</p>
              <div className="grid grid-cols-2 gap-2">
                {INDIVIDUAL_PROFESSIONS.map(p => (
                  <button key={p.id} onClick={() => setProfession(p.id)}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left transition-all cursor-pointer
                      ${profession === p.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 text-slate-700'}`}>
                    <span className={profession === p.id ? 'text-blue-600' : 'text-slate-400'}>{p.icon}</span>
                    <span className="text-xs font-bold">{p.label}</span>
                    {profession === p.id && <CheckCircle className="w-3.5 h-3.5 ml-auto text-blue-500 shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── INDIVIDUAL Step 2: Details ── */}
          {!isOrg && step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Your Name / Display Name *</label>
                <input type="text" value={displayName} onChange={e => { setDisplayName(e.target.value); setError(''); }}
                  placeholder="Rihan Rahiman" autoFocus
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">
                  Phone Number <span className="text-slate-400 font-normal normal-case">(optional)</span>
                </label>
                <PhoneInput value={phone} onChange={setPhone} />
              </div>
            </div>
          )}

          {/* ── ORG Step 1: Company ── */}
          {isOrg && step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Company / Organization Name *</label>
                <input type="text" value={companyName} onChange={e => { setCompanyName(e.target.value); setError(''); }}
                  placeholder="Acme Technologies Pvt Ltd" autoFocus
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">
                  Website <span className="text-slate-400 font-normal normal-case">(optional)</span>
                </label>
                <input type="url" value={website} onChange={e => setWebsite(e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          )}

          {/* ── ORG Step 2: Industry ── */}
          {isOrg && step === 2 && (
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">What industry are you in?</p>
              <div className="grid grid-cols-2 gap-2">
                {ORG_INDUSTRIES.map(ind => (
                  <button key={ind.id} onClick={() => setIndustry(ind.id)}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left transition-all cursor-pointer
                      ${industry === ind.id ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300 text-slate-700'}`}>
                    <span className={industry === ind.id ? 'text-purple-600' : 'text-slate-400'}>{ind.icon}</span>
                    <span className="text-xs font-bold">{ind.label}</span>
                    {industry === ind.id && <CheckCircle className="w-3.5 h-3.5 ml-auto text-purple-500 shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── ORG Step 3: Team & Volume ── */}
          {isOrg && step === 3 && (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">Team size</p>
                <div className="flex flex-wrap gap-2">
                  {TEAM_SIZES.map(s => (
                    <button key={s} onClick={() => setTeamSize(s)}
                      className={`px-4 py-2 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer
                        ${teamSize === s ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">Monthly invoice volume</p>
                <div className="flex flex-wrap gap-2">
                  {INVOICE_VOLUMES.map(v => (
                    <button key={v} onClick={() => setInvoiceVolume(v)}
                      className={`px-4 py-2 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer
                        ${invoiceVolume === v ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>{v}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── LAST STEP: Assigned Prefix Display ── */}
          {step === invoiceStep && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1">Your Invoice Prefix</p>
                <p className="text-[11px] text-slate-400 mb-4">
                  This prefix is permanently assigned to your account. It cannot be changed and will appear on every invoice and receipt you generate.
                </p>

                {prefixLoading && (
                  <div className="flex items-center gap-3 py-6 justify-center text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm font-medium">Assigning your prefix…</span>
                  </div>
                )}

                {prefixError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-semibold px-3 py-2.5 rounded-lg mb-3">
                    {prefixError}
                  </div>
                )}

                {!prefixLoading && invoicePrefix && (
                  <>
                    {/* Prefix cards */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Lock className="w-3 h-3 text-amber-500" />
                          <span className="text-[9px] font-black uppercase tracking-wider text-amber-600">Invoice Prefix</span>
                        </div>
                        <p className="text-2xl font-black font-mono text-amber-800 tracking-wide">{invoicePrefix}</p>
                        <p className="text-[10px] text-amber-500 mt-1 font-mono">e.g. {invoicePrefix}-000001</p>
                      </div>
                      <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Lock className="w-3 h-3 text-purple-500" />
                          <span className="text-[9px] font-black uppercase tracking-wider text-purple-600">Receipt Prefix</span>
                        </div>
                        <p className="text-2xl font-black font-mono text-purple-800 tracking-wide">{receiptPrefix}</p>
                        <p className="text-[10px] text-purple-500 mt-1 font-mono">e.g. {receiptPrefix}-00001</p>
                      </div>
                    </div>

                    {/* Lock warning */}
                    <div className="bg-slate-900 rounded-xl p-4 flex gap-3">
                      <Lock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-bold text-white mb-0.5">These are permanently yours</p>
                        <p className="text-[10px] text-slate-400 leading-relaxed">
                          Your prefixes are unique to your account and locked forever. No other user can claim <span className="text-emerald-400 font-mono font-bold">{invoicePrefix}</span> or <span className="text-blue-400 font-mono font-bold">{receiptPrefix}</span>. They will always appear in your Profile.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-semibold px-3 py-2.5 rounded-lg">{error}</div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-1">
            {step > 1 && (
              <button onClick={back}
                className="flex items-center gap-1.5 px-4 py-2.5 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all cursor-pointer">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            {step < totalSteps ? (
              <button onClick={next} disabled={!canNext()}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm transition-all cursor-pointer">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={saving || prefixLoading || !invoicePrefix}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-60 text-white rounded-xl font-bold text-sm transition-all cursor-pointer">
                {saving ? 'Saving…' : <><Rocket className="w-4 h-4" /> Launch workspace</>}
              </button>
            )}
          </div>

          {!isOrg && (
            <button onClick={onComplete}
              className="w-full text-center text-xs text-slate-400 hover:text-slate-600 transition-colors">
              Skip for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
